import type { Nullable } from "models-webgl2";
import { CommandAction } from "utils/helpers/classes/command-controller";
import {
  Webgl2Component,
  CropSelectionComponent,
  ImageRectComponent,
  ImageAdjustRectComponent,
} from "../../webgl2-component";
import { Texture } from "../../texture";
import { Webgl2dRenderer } from "../webgl2d-renderer";
import { Webgl2Tool } from "./webgl2-tool";
import { Vec2 } from "utils/math/vec2";
import { Mat3 } from "utils/math/mat3";

export class CropTool extends Webgl2Tool {
  private _currComponent: Nullable<Webgl2Component>;
  private _dispose: Nullable<VoidFunction>;

  private get _selectedComponent(): Nullable<ImageRectComponent | ImageAdjustRectComponent> {
    const component = this._renderer.selectedComponent;
    return component instanceof ImageRectComponent || component instanceof ImageAdjustRectComponent
      ? component
      : null;
  }

  constructor(private readonly _renderer: Webgl2dRenderer) {
    super();
  }

  public onAssign() {
    if (!this._selectedComponent) return;
    this._renderer.cropSelectionComponent.copyComponentTransform(this._selectedComponent);
    this._dispose = this._renderer.commands.events.on("history", () => {
      this._renderer.eventsManager.assignTool(this._renderer.tools.handSelectionTool);
    });
  }

  public onUnAssign() {
    this._handleClearTool();
  }

  public handleCrop() {
    const component = this._selectedComponent;
    if (!component) return this._handleClearTool();

    const ratio = component.state.texture.textureSize.clone().divide(component.transform.size);
    let cropSize = this._renderer.cropSelectionComponent.transform.size;
    if (cropSize.hasZero() || cropSize.equals(component.transform.size)) {
      return this._handleClearTool();
    }
    cropSize = cropSize.clone().multiply(ratio);

    const undoTexture = component.state.texture.clone();
    const undoTransform = component.transform.clone();

    const actionTexture = component.state.texture.clone();
    const selectedTopLeft = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      component,
      Vec2.zero(),
    );
    const cropTopLeft = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      this._renderer.cropSelectionComponent,
      Vec2.zero(),
    );
    const cropOffset = cropTopLeft
      .transformMat3(
        Mat3.identity()
          .rotate(-component.transform.rads)
          .translate(selectedTopLeft.clone().negate()).mat,
      )
      .multiply(ratio);

    Texture.cropTexture(
      component.state.texture,
      actionTexture,
      cropOffset.x,
      cropOffset.y,
      cropSize.width,
      cropSize.height,
    );
    const actionTransform = this._renderer.cropSelectionComponent.transform.clone();

    this._renderer.commands.execute(
      new CommandAction(
        "Crop Tool",
        () => {
          this._renderer.selectComponent(component);
          component.transform.copy(actionTransform);
          component.state.texture = actionTexture.clone();
          this._renderer.eventsManager.assignTool(this._renderer.tools.handSelectionTool);
          this._renderer.selectionComponent.copyComponentTransform(component);
        },
        () => {
          this._renderer.selectComponent(component);
          component.transform.copy(undoTransform);
          component.state.texture = undoTexture.clone();
          this._renderer.eventsManager.assignTool(this._renderer.tools.cropTool);
          this._renderer.cropSelectionComponent.copyComponentTransform(component);
          this._renderer.selectionComponent.copyComponentTransform(component);
        },
      ),
    );
  }

  public onMouseDown(e: MouseEvent) {
    return this._handleDown(e);
  }

  public onMouseMove(e: MouseEvent) {
    this._handleMove(e);
  }

  public onMouseEnded(e: MouseEvent) {
    return this._handleUp(e);
  }

  public onTouchStart(e: TouchEvent) {
    return this._handleDown(e);
  }

  public onTouchMove(e: TouchEvent) {
    this._handleMove(e);
  }

  public onTouchEnded(e: TouchEvent) {
    return this._handleUp(e);
  }

  private _handleDown(e: MouseEvent | TouchEvent) {
    this._currComponent = this._renderer.findComponentUnderMouse(e);

    const parent = Webgl2dRenderer.global.getRootParent(this._currComponent).slice(-1)[0];

    if (parent instanceof CropSelectionComponent && this._currComponent?.onMouseDown) {
      this._currComponent?.onMouseDown(e);
    } else {
      this._renderer.tools.handTool.handleDown(e);
    }
  }

  private _handleMove(e: MouseEvent | TouchEvent) {
    if (this._currComponent) {
      this._currComponent.onMouseMove?.(e);
    } else {
      this._renderer.tools.handTool.handleMove(e);
    }
  }

  private _handleUp(e: MouseEvent | TouchEvent) {
    this._currComponent?.onMouseUp?.(e);
  }

  private _handleClearTool() {
    this._renderer.cropSelectionComponent.transform.reset();
    this._renderer.eventsManager.assignTool(this._renderer.tools.handSelectionTool);
    this._dispose?.();
    this._dispose = null;
  }
}

export default CropTool;
